import { ListItem } from '@mui/material';
import * as React from 'react';
import { ConnectDragSource, ConnectDropTarget, DragSource, DragSourceConnector, DragSourceMonitor, DragSourceSpec, DropTarget, DropTargetConnector, DropTargetMonitor, DropTargetSpec } from 'react-dnd';
import { findDOMNode } from 'react-dom';
import ItemTypes from './ItemTypes';

/////////////////////////////////////////////////////////////////////////////////

const style = {
    cursor: 'move',
};

/////////////////////////////////////////////////////////////////////////////////


interface IDragObjectProps {
    id: string | number;
    index: number
}

interface IProps {
    index: number,
    id: string | number,
    content: string | React.ReactNode,
    className?: string,
    onClick?: () => void,
    onMoveListItem?: (dragIndex: number, hoverIndex: number) => void,
}

interface IWithDropTarget {
    connectDropTarget?: ConnectDropTarget,
}
interface IWithDropSource {
    connectDragSource?: ConnectDragSource,
    isDragging?: boolean,
}

type Props = IProps & IWithDropTarget & IWithDropSource;


/////////////////////////////////////////////////////////////////////////////////

const cardSource: DragSourceSpec<IProps, IDragObjectProps> = {
    beginDrag(props: IProps): IDragObjectProps {
        return {
            id: props.id,
            index: props.index,
        };
    },
};

const cardTarget: DropTargetSpec<IProps> = {
    hover(props: IProps, monitor: DropTargetMonitor, component: any) {
        const dragIndex = (monitor.getItem() as any).index;
        const hoverIndex = props.index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
            return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = (findDOMNode(component) as Element).getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
            return;
        }

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
            return;
        }

        // Time to actually perform the action
        props.onMoveListItem(dragIndex, hoverIndex);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        (monitor.getItem() as any).index = hoverIndex;
    }
};

const withDropTarget = DropTarget<IProps>(
    ItemTypes.LIST_ITEM,
    cardTarget,
    (connect: DropTargetConnector): IWithDropTarget => ({
        connectDropTarget: connect.dropTarget()
    })
);

const withDropSource = DragSource<IProps>(
    ItemTypes.LIST_ITEM,
    cardSource,
    (connect: DragSourceConnector, monitor: DragSourceMonitor): IWithDropSource => ({
        connectDragSource: connect.dragSource(),
        isDragging: monitor.isDragging(),
    })
);


/////////////////////////////////////////////////////////////////////////////////

class ReorderListItem extends React.PureComponent<Props> {

    render() {
        const {
            content,
            className,

            isDragging,
            connectDragSource,
            connectDropTarget,
            // props for onClick selection
            onClick
            //
        } = this.props;

        const opacity = isDragging ? 0 : 1;

        return connectDragSource(
            connectDropTarget(
                <div style={{ ...style, opacity }} className={className}>
                    <ListItem onClick={() => onClick()}>{content}</ListItem>
                </div>)
        );
    }
}

export default withDropTarget(withDropSource(ReorderListItem));